import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiPlay, HiLocationMarker, HiClock, HiArrowRight, HiCalendar } from 'react-icons/hi';
import { IoRadioButtonOn } from 'react-icons/io5';
import './NCLive.css';

function CrestLogo({ className }) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/nudgee-college-crest.png`}
      alt="Nudgee College"
      className={`nc-crest-logo ${className || ''}`}
    />
  );
}

export default function NCLive({ config }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGames();
    const interval = setInterval(loadGames, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadGames = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games`);
      const data = await res.json();
      const all = data.data.games || [];
      setGames(all.sort((a, b) => {
        if (a.status === 'live' && b.status !== 'live') return -1;
        if (b.status === 'live' && a.status !== 'live') return 1;
        return new Date(b.scheduledStart) - new Date(a.scheduledStart);
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const liveGames = games.filter(g => g.status === 'live');
  const upcomingGames = games.filter(g => g.status === 'scheduled' || g.status === 'ready');
  const recordedGames = games.filter(g => g.status === 'recorded' && g.recordingUrl);

  if (loading) return (
    <div className="nc-loading">
      <CrestLogo className="nc-loading-crest" />
      <span>NC LIVE</span>
    </div>
  );

  return (
    <div className="nc">

      {/* Hero */}
      <section className="nc-hero">
        <div className="nc-hero-stripes" />
        <div className="nc-hero-crest" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/nudgee-college-crest.png)` }} />
        <div className="nc-hero-overlay" />
        <div className="nc-hero-content">
          <div className="nc-hero-eyebrow">
            {liveGames.length > 0
              ? <><IoRadioButtonOn className="nc-live-pulse" /> {liveGames.length} GAME{liveGames.length > 1 ? 'S' : ''} LIVE NOW</>
              : 'ST JOSEPH\'S NUDGEE COLLEGE'
            }
          </div>
          <h1 className="nc-hero-headline">
            NUDGEE<br />
            COLLEGE<br />
            <span className="nc-hero-accent">LIVE SPORTS</span>
          </h1>
          <p className="nc-hero-tagline">Live. Replay. Nudgee.</p>
          {liveGames.length > 0 && (
            <Link to={`/watch/${liveGames[0].gameId}`} className="nc-hero-btn">
              <HiPlay /> Watch Live <HiArrowRight />
            </Link>
          )}
        </div>
        {liveGames[0] && (
          <Link to={`/watch/${liveGames[0].gameId}`} className="nc-hero-live-card">
            <div className="nc-hero-live-badge"><IoRadioButtonOn /> LIVE</div>
            <div className="nc-hero-live-teams">
              {liveGames[0].homeTeam} <span>vs</span> {liveGames[0].awayTeam}
            </div>
            <div className="nc-hero-live-venue"><HiLocationMarker /> {liveGames[0].venue}</div>
          </Link>
        )}
      </section>

      <div className="nc-body">

        {/* Live Games */}
        {liveGames.length > 0 && (
          <section className="nc-section">
            <div className="nc-section-header">
              <h2><IoRadioButtonOn className="nc-live-pulse" /> LIVE NOW</h2>
            </div>
            <div className="nc-live-grid">
              {liveGames.map(game => (
                <Link key={game.gameId} to={`/watch/${game.gameId}`} className="nc-live-card">
                  <div className="nc-card-shine" />
                  <div className="nc-live-badge"><IoRadioButtonOn /> LIVE</div>
                  <div className="nc-card-sport">{game.sport?.toUpperCase()}</div>
                  <div className="nc-card-teams">
                    <span>{game.homeTeam}</span>
                    <span className="nc-vs">VS</span>
                    <span>{game.awayTeam}</span>
                  </div>
                  <div className="nc-card-venue"><HiLocationMarker /> {game.venue}</div>
                  <div className="nc-card-cta"><HiPlay /> WATCH LIVE <HiArrowRight /></div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming */}
        {upcomingGames.length > 0 && (
          <section className="nc-section">
            <div className="nc-section-header">
              <h2><HiCalendar /> UPCOMING</h2>
            </div>
            <div className="nc-upcoming-list">
              {upcomingGames.slice(0, 8).map((game, i) => (
                <div key={game.gameId} className="nc-upcoming-row">
                  <div className="nc-upcoming-num">0{i + 1}</div>
                  <div className="nc-upcoming-sport">{game.sport?.toUpperCase()}</div>
                  <div className="nc-upcoming-teams">
                    {game.homeTeam} <span>vs</span> {game.awayTeam}
                  </div>
                  <div className="nc-upcoming-meta">
                    <span><HiLocationMarker /> {game.venue}</span>
                    <span><HiClock /> {new Date(game.scheduledStart).toLocaleString('en-AU', {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit'
                    })}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Replays preview */}
        {recordedGames.length > 0 && (
          <section className="nc-section">
            <div className="nc-section-header">
              <h2><HiPlay /> REPLAYS</h2>
              <Link to="/replays" className="nc-section-more">View All <HiArrowRight /></Link>
            </div>
            <div className="nc-replays-grid">
              {recordedGames.slice(0, 3).map(game => (
                <Link key={game.gameId} to={`/watch/${game.gameId}`} className="nc-replay-card">
                  <div className="nc-replay-thumb">
                    {game.thumbnailUrl
                      ? <img src={game.thumbnailUrl} alt="Match" />
                      : <div className="nc-replay-placeholder" />
                    }
                    <div className="nc-replay-overlay"><HiPlay className="nc-replay-play" /></div>
                    <div className="nc-replay-stripe" />
                  </div>
                  <div className="nc-replay-info">
                    <div className="nc-card-sport">{game.sport?.toUpperCase()}</div>
                    <div className="nc-replay-teams">{game.homeTeam} vs {game.awayTeam}</div>
                    <div className="nc-card-venue"><HiLocationMarker /> {game.venue}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {games.length === 0 && (
          <div className="nc-empty">
            <CrestLogo />
            <h2>NO MATCHES SCHEDULED</h2>
            <p>Check back soon for live action</p>
          </div>
        )}

      </div>

      <footer className="nc-footer">
        <CrestLogo />
        <span>NC LIVE · St Joseph's Nudgee College · <em>Signum Fidei</em></span>
      </footer>
    </div>
  );
}
